/* ── NurseNote Feeding Tracker — Adaptive 0–24m ─────────────────────────── */
'use strict';

// ── Storage keys ─────────────────────────────────────────────────────────────
const DB_KEY           = 'ft_sessions';
const FOOD_KEY         = 'ft_food_logs';
const ALLERGEN_KEY     = 'ft_allergens';
const PREFS_KEY        = 'ft_prefs';
const ACTIVE_NURSE_KEY = 'ft_active_nurse';
const PUMP_KEY         = 'ft_pump_logs';

// ── Top-9 allergen registry ───────────────────────────────────────────────────
const TOP9_ALLERGENS = [
  { key: 'peanut',    label: 'Peanut',     keywords: ['peanut', 'peanuts', 'pb', 'peanut butter'] },
  { key: 'egg',       label: 'Egg',        keywords: ['egg', 'eggs', 'scrambled egg', 'boiled egg'] },
  { key: 'milk',      label: "Cow's Milk", keywords: ['milk', 'dairy', 'cheese', 'yogurt', 'yoghurt', 'butter', 'cream', "cow's milk", 'cows milk'] },
  { key: 'wheat',     label: 'Wheat',      keywords: ['wheat', 'bread', 'pasta', 'flour', 'cereal', 'cracker', 'biscuit', 'oat', 'oats'] },
  { key: 'soy',       label: 'Soy',        keywords: ['soy', 'soya', 'tofu', 'edamame', 'soy milk'] },
  { key: 'tree_nut',  label: 'Tree Nut',   keywords: ['almond', 'cashew', 'walnut', 'pecan', 'hazelnut', 'pistachio', 'macadamia', 'tree nut', 'nut butter'] },
  { key: 'fish',      label: 'Fish',       keywords: ['fish', 'salmon', 'cod', 'tuna', 'tilapia', 'trout', 'haddock', 'halibut', 'sardine'] },
  { key: 'shellfish', label: 'Shellfish',  keywords: ['shellfish', 'shrimp', 'crab', 'lobster', 'clam', 'oyster', 'scallop', 'prawn'] },
  { key: 'sesame',    label: 'Sesame',     keywords: ['sesame', 'tahini', 'hummus'] },
];

// ── Stage definitions (months) ────────────────────────────────────────────────
const STAGES = [
  { id: 1, label: 'Newborn',       min: 0,  max: 3  },
  { id: 2, label: 'Early Solids',  min: 3,  max: 6  },
  { id: 3, label: 'Solids',        min: 6,  max: 12 },
  { id: 4, label: 'Toddler',       min: 12, max: 18 },
  { id: 5, label: '18m+',          min: 18, max: 25 },
];

// ── State ─────────────────────────────────────────────────────────────────────
let sessions   = [];
let pumpLogs   = [];
let foodLogs   = [];
let allergens  = {};
let prefs      = { babyName: '', birthdate: '', feedingMethod: 'breast', notifEnabled: false, onboardingDone: false, weaningPromptShown: false, solidsActivated: false };
let screen     = 'home';
let nurseTimer = null;
let voiceRec   = null;

// ── DOM helpers ───────────────────────────────────────────────────────────────
const $ = (sel, ctx = document) => ctx.querySelector(sel);
const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

function el(tag, cls, html) {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (html !== undefined) e.innerHTML = html;
  return e;
}

// ── Storage ───────────────────────────────────────────────────────────────────
function loadSessions() {
  try { sessions = JSON.parse(localStorage.getItem(DB_KEY) || '[]'); }
  catch { sessions = []; }
}

function saveSessions() { localStorage.setItem(DB_KEY, JSON.stringify(sessions)); syncToCloud(); }

function loadPumpLogs() {
  try { pumpLogs = JSON.parse(localStorage.getItem(PUMP_KEY) || '[]'); }
  catch { pumpLogs = []; }
}

function savePumpLogs() { localStorage.setItem(PUMP_KEY, JSON.stringify(pumpLogs)); syncToCloud(); }

function loadFoodLogs() {
  try { foodLogs = JSON.parse(localStorage.getItem(FOOD_KEY) || '[]'); }
  catch { foodLogs = []; }
}

function saveFoodLogs() { localStorage.setItem(FOOD_KEY, JSON.stringify(foodLogs)); syncToCloud(); }

function loadAllergens() {
  try { allergens = JSON.parse(localStorage.getItem(ALLERGEN_KEY) || '{}'); }
  catch { allergens = {}; }
}

function saveAllergens() { localStorage.setItem(ALLERGEN_KEY, JSON.stringify(allergens)); syncToCloud(); }

function loadPrefs() {
  try { prefs = { ...prefs, ...JSON.parse(localStorage.getItem(PREFS_KEY) || '{}') }; }
  catch { /* keep defaults */ }
}

function savePrefs() { localStorage.setItem(PREFS_KEY, JSON.stringify(prefs)); syncToCloud(); }

function loadActiveNurse() {
  try { return JSON.parse(localStorage.getItem(ACTIVE_NURSE_KEY) || 'null'); }
  catch { return null; }
}

function saveActiveNurse(state) {
  if (state) localStorage.setItem(ACTIVE_NURSE_KEY, JSON.stringify(state));
  else localStorage.removeItem(ACTIVE_NURSE_KEY);
}

// ── Cloud sync (birch_tracker.feeding metafield, per-child) ──────────────────
const CLOUD_BASE   = '/apps/ai/tracker/feeding';
const TRIM_MS      = 30 * 24 * 60 * 60 * 1000; // keep 30 days
let _syncTimer     = null;
// Active tracker context — set after boot
// type: 'own' | 'shared'
let _trackerContext = { type: 'own', childId: null, childName: null, ownerId: null, ownerName: null };
let _allContexts    = []; // all own + shared contexts

// For backward compat with hydrateFromSharedProfile
let _activeChildId = null;
let _allChildren   = [];

function cloudApi() {
  if (_trackerContext.type === 'shared' && _trackerContext.ownerId) {
    return `/apps/ai/tracker-caregivers/data/feeding?ownerId=${encodeURIComponent(_trackerContext.ownerId)}`;
  }
  const cid = _trackerContext.childId || _activeChildId;
  return cid ? `${CLOUD_BASE}?childId=${encodeURIComponent(cid)}` : CLOUD_BASE;
}

function _cloudPayload() {
  const cutoff = Date.now() - TRIM_MS;
  return {
    sessions:  sessions.filter(s => s.endedAt >= cutoff),
    pumpLogs:  pumpLogs.filter(p => p.endedAt >= cutoff),
    foodLogs:  foodLogs.filter(f => (f.endedAt || f.ts || 0) >= cutoff),
    allergens,
    prefs,
    updatedAt: Date.now(),
  };
}

function syncToCloud() {
  clearTimeout(_syncTimer);
  _syncTimer = setTimeout(() => {
    fetch(cloudApi(), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(_cloudPayload()),
    }).catch(() => { /* localStorage is always the fallback */ });
  }, 400);
}

async function loadFromCloud() {
  try {
    const r = await fetch(cloudApi(), { credentials: 'same-origin' });
    if (!r.ok) return;
    const { data } = await r.json();
    if (!data || typeof data !== 'object') return;
    if (Array.isArray(data.sessions))  { sessions  = data.sessions;  localStorage.setItem(DB_KEY,       JSON.stringify(sessions));  }
    if (Array.isArray(data.pumpLogs))  { pumpLogs  = data.pumpLogs;  localStorage.setItem(PUMP_KEY,     JSON.stringify(pumpLogs));  }
    if (Array.isArray(data.foodLogs))  { foodLogs  = data.foodLogs;  localStorage.setItem(FOOD_KEY,     JSON.stringify(foodLogs));  }
    if (data.allergens && typeof data.allergens === 'object') { allergens = data.allergens; localStorage.setItem(ALLERGEN_KEY, JSON.stringify(allergens)); }
    if (data.prefs     && typeof data.prefs     === 'object') { prefs     = { ...prefs, ...data.prefs }; localStorage.setItem(PREFS_KEY,  JSON.stringify(prefs));     }
  } catch { /* silent — local data stays */ }
}

// ── Stage detection ───────────────────────────────────────────────────────────
function getAgeMonths() {
  if (!prefs.birthdate) return 0;
  const birth = new Date(prefs.birthdate);
  const now   = new Date();
  return (now.getFullYear() - birth.getFullYear()) * 12 + (now.getMonth() - birth.getMonth());
}

function getStage() {
  const months = getAgeMonths();
  for (const s of STAGES) {
    if (months >= s.min && months < s.max) return s.id;
  }
  return months < 0 ? 1 : 5;
}

function getStageLabel() {
  const stage = getStage();
  return STAGES.find(s => s.id === stage)?.label || '';
}

// ── Time utilities ────────────────────────────────────────────────────────────
function msToMMSS(ms) {
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  return `${String(m).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

function msToHMM(ms) {
  const totalMin = Math.round(ms / 60000);
  if (totalMin < 60) return `${totalMin}m`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

function formatTimeAgo(ts) {
  const diff = Date.now() - ts;
  const m = Math.round(diff / 60000);
  if (m < 1)  return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  if (h < 24) return rem === 0 ? `${h}h ago` : `${h}h ${rem}m ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function formatTime(ts) {
  return new Date(ts).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function formatDate(ts) {
  const d = new Date(ts);
  const today = new Date();
  const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' });
}

// ── Session helpers ───────────────────────────────────────────────────────────
function newId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
}

function todaySessions() {
  const midnight = new Date(); midnight.setHours(0, 0, 0, 0);
  return sessions.filter(s => s.endedAt >= midnight.getTime());
}

function todayFoodLogs() {
  const midnight = new Date(); midnight.setHours(0, 0, 0, 0);
  return foodLogs.filter(f => f.ts >= midnight.getTime());
}

function lastSession() {
  return sessions.length ? sessions[sessions.length - 1] : null;
}

function saveSession(sess) {
  sessions.push(sess);
  saveSessions();
}

// ── Navigation ────────────────────────────────────────────────────────────────
function navigate(name, { pushHistory = true } = {}) {
  const prev = screen;
  screen = name;
  $$('.ft-screen').forEach(s => s.classList.remove('ft-screen--active'));
  $$('.ft-nav-item').forEach(btn => btn.classList.remove('ft-nav-item--active'));

  const scr = $(`#ft-screen-${name}`);
  if (scr) scr.classList.add('ft-screen--active');

  const navBtn = $(`.ft-nav-item[data-screen="${name}"]`);
  if (navBtn) navBtn.classList.add('ft-nav-item--active');

  if (name === 'home')     renderHome();
  if (name === 'history')  renderHistory();
  if (name === 'insights') renderInsights();

  // Push a history entry when moving away from home so the browser back
  // button returns to the previous screen instead of leaving the tracker.
  if (pushHistory && name !== 'home' && prev !== name) {
    history.pushState({ ftScreen: name }, '');
  }
}

window.addEventListener('popstate', function(e) {
  if (e.state && e.state.ftScreen) {
    navigate(e.state.ftScreen, { pushHistory: false });
  } else if (screen !== 'home') {
    navigate('home', { pushHistory: false });
  }
});

function openOverlay(id) {
  const ov = $(`#${id}`);
  if (ov) ov.classList.add('ft-overlay--active');
}

function closeOverlay(id) {
  const ov = $(`#${id}`);
  if (ov) ov.classList.remove('ft-overlay--active');
}

// ── Toast ─────────────────────────────────────────────────────────────────────
function showToast(msg, duration = 2800) {
  let toast = $('#ft-toast');
  if (!toast) {
    toast = el('div', 'ft-toast');
    toast.id = 'ft-toast';
    document.body.appendChild(toast);
  }
  toast.textContent = msg;
  toast.classList.add('ft-toast--show');
  clearTimeout(toast._timer);
  toast._timer = setTimeout(() => toast.classList.remove('ft-toast--show'), duration);
}

// ── Log picker ────────────────────────────────────────────────────────────────
function openLogPicker()  { openOverlay('ft-log-picker'); }
function closeLogPicker() { closeOverlay('ft-log-picker'); }

// ── ONBOARDING ────────────────────────────────────────────────────────────────

/** Try to hydrate prefs from the shared child profile, returns true if found. */
async function hydrateFromSharedProfile() {
  try {
    // Build all contexts (own children + shared caregiver children)
    const BC  = window.BirchChildren;
    const BTC = window.BirchCaregivers;
    const ownChildren = BC ? await BC.load() : [];
    _allChildren = ownChildren;

    const allCtxs = BTC ? await BTC.getAllContexts('feeding') : ownChildren.map(c => ({
      type: 'own', childId: c.id, childName: c.name, dob: c.dob, ownerId: null, ownerName: null, label: c.name
    }));
    _allContexts = allCtxs;

    if (allCtxs.length === 0) return false;

    let ctx;
    if (allCtxs.length === 1) {
      ctx = allCtxs[0];
    } else if (prefs.onboardingDone && _trackerContext.childId) {
      // Keep existing selection
      ctx = allCtxs.find(c => c.childId === _trackerContext.childId) || allCtxs[0];
    } else if (!prefs.onboardingDone) {
      ctx = BC ? await BC.pickContext(allCtxs, { title: 'Whose feeding are you tracking?' }) : allCtxs[0];
    } else {
      ctx = allCtxs[0];
    }
    if (!ctx) return prefs.onboardingDone;

    _trackerContext = ctx;
    _activeChildId  = ctx.childId || null;
    prefs.babyName       = ctx.childName || prefs.babyName;
    prefs.birthdate      = ctx.dob || prefs.birthdate;
    prefs.onboardingDone = true;
    savePrefs();
    return true;
  } catch (e) {
    return false;
  }
}

/** Open the unified context picker (own children + shared) and switch context. */
async function switchActiveChild() {
  if (_allContexts.length < 2) return;
  const ctx = await (window.BirchChildren && window.BirchChildren.pickContext
    ? window.BirchChildren.pickContext(_allContexts, { title: 'Whose feeding tracker?' })
    : Promise.resolve(null));
  if (!ctx) return;

  _trackerContext = ctx;
  _activeChildId  = ctx.childId || null;
  prefs.babyName  = ctx.childName || '';
  prefs.birthdate = ctx.dob || prefs.birthdate;
  savePrefs();

  // Reset in-memory data and reload from the selected context's cloud endpoint
  sessions = []; foodLogs = []; allergens = {}; pumpLogs = [];
  await loadFromCloud();
  updateTopbar();
  applyStageUI(getStage());
  navigate(screen);
  const who = ctx.type === 'shared'
    ? `${ctx.childName}'s tracker (${ctx.ownerName}'s)`
    : `${ctx.childName}'s tracker`;
  showToast(`Switched to ${who}`);
}

function checkOnboarding() {
  if (!prefs.onboardingDone) {
    openOverlay('ft-onboarding');
  }
}

function saveOnboarding() {
  const nameEl = $('#ft-ob-name');
  const bdEl   = $('#ft-ob-birthdate');
  const fmBtn  = $('.ft-ob-fm--active');

  const name      = nameEl?.value.trim() || '';
  const birthdate = bdEl?.value || '';
  const fm        = fmBtn?.dataset.fm || 'breast';

  if (!birthdate) { showToast('Please enter baby\'s date of birth.'); return; }

  prefs.babyName       = name;
  prefs.birthdate      = birthdate;
  prefs.feedingMethod  = fm;
  prefs.onboardingDone = true;
  savePrefs();

  // Sync child to shared profile so other trackers don't ask again
  if (window.BirchChildren) {
    window.BirchChildren.upsert({
      id: window.BirchChildren.newId(),
      name: name || 'Baby',
      dob: birthdate,
    }).catch(function() {});
  }

  closeOverlay('ft-onboarding');
  applyStageUI(getStage());
  updateTopbar();
  renderHome();
}

// ── STAGE UI ──────────────────────────────────────────────────────────────────
function applyStageUI(stage) {
  const show = (id, visible) => {
    const el = $(`#${id}`);
    if (el) el.style.display = visible ? '' : 'none';
  };

  const ageMonths = getAgeMonths();

  // Age-tier quick log button visibility
  // 0–4 months: Nursing, Bottle, Pump only
  // 4–12 months: + Food
  // 12+ months: Meal, Food, Bottle, Pump, Nursing, Water (reordered via CSS order)
  if (ageMonths < 4) {
    show('ft-ql-nurse',  true);
    show('ft-ql-bottle', true);
    show('ft-ql-pump',   true);
    show('ft-ql-food',   false);
    show('ft-ql-meal',   false);
    show('ft-ql-water',  false);
  } else if (ageMonths < 12) {
    show('ft-ql-nurse',  true);
    show('ft-ql-bottle', true);
    show('ft-ql-pump',   true);
    show('ft-ql-food',   true);
    show('ft-ql-meal',   false);
    show('ft-ql-water',  false);
  } else {
    show('ft-ql-nurse',  true);
    show('ft-ql-bottle', true);
    show('ft-ql-pump',   true);
    show('ft-ql-food',   true);
    show('ft-ql-meal',   true);
    show('ft-ql-water',  true);
    const nurseBtn  = $('#ft-ql-nurse');
    const bottleBtn = $('#ft-ql-bottle');
    const pumpBtn   = $('#ft-ql-pump');
    const foodBtn   = $('#ft-ql-food');
    const mealBtn   = $('#ft-ql-meal');
    const waterBtn  = $('#ft-ql-water');
    if (mealBtn)   mealBtn.style.order   = '1';
    if (foodBtn)   foodBtn.style.order   = '2';
    if (bottleBtn) bottleBtn.style.order = '3';
    if (pumpBtn)   pumpBtn.style.order   = '4';
    if (nurseBtn)  nurseBtn.style.order  = '5';
    if (waterBtn)  waterBtn.style.order  = '6';
  }

  // Log picker items
  show('ft-pick-food',  ageMonths >= 4);
  show('ft-pick-meal',  ageMonths >= 12);
  show('ft-pick-water', ageMonths >= 12);

  // History filter chips
  $$('.ft-filter-chip--stage').forEach(chip => {
    chip.style.display = stage >= 2 ? '' : 'none';
  });

  // Meal chip (only stage 3+)
  const mealChip = $('.ft-filter-chip[data-filter="meal"]');
  if (mealChip) mealChip.style.display = stage >= 3 ? '' : 'none';

  // Cow's milk bottle option at stage 4+
  show('ft-type-cows-milk', stage >= 4);

  // Stage badge
  const badge = $('.ft-topbar__stage');
  if (badge) {
    badge.textContent = getStageLabel();
    badge.style.display = prefs.onboardingDone ? '' : 'none';
  }

  // Allergen passport (history screen)
  if (stage >= 2) renderAllergenPassport();
}

// ── TOP BAR ───────────────────────────────────────────────────────────────────
function updateTopbar() {
  const hour = new Date().getHours();
  const timeGreet = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';

  // Greet the parent by name if available, otherwise time-based greeting
  const userName = window.__ftUserName || '';
  const greetingEl = $('.ft-topbar__greeting');
  if (greetingEl) greetingEl.textContent = userName ? `${timeGreet}, ${userName}` : timeGreet;

  // Show "<BabyName>'s Feeding Tracker"
  const nameEl = $('.ft-topbar__name');
  if (nameEl) {
    const baby = prefs.babyName;
    nameEl.textContent = baby ? `${baby}'s Feeding Tracker` : 'Feeding Tracker';
  }

  // Show context switcher when there are multiple own/shared contexts
  const switchBtn = $('#ft-switch-child-btn');
  if (switchBtn) switchBtn.style.display = _allContexts.length > 1 ? '' : 'none';
}

// ── NURSE OVERLAY ─────────────────────────────────────────────────────────────
function getNurseTotals(state) {
  const now = Date.now();
  let leftMs  = state.leftAccumulatedMs || 0;
  let rightMs = state.rightAccumulatedMs || 0;
  if (!state.paused) {
    if (state.activeSide === 'left'  && state.leftStartedAt)  leftMs  += now - state.leftStartedAt;
    if (state.activeSide === 'right' && state.rightStartedAt) rightMs += now - state.rightStartedAt;
  }
  return { leftMs, rightMs, totalMs: leftMs + rightMs };
}

function openNurseOverlay(initialSide = 'left') {
  let state = loadActiveNurse();
  if (!state) {
    state = {
      side: initialSide,
      activeSide: initialSide,
      leftStartedAt:  initialSide === 'left'  ? Date.now() : null,
      rightStartedAt: initialSide === 'right' ? Date.now() : null,
      leftAccumulatedMs: 0,
      rightAccumulatedMs: 0,
      sessionStartedAt: Date.now(),
      paused: false
    };
    saveActiveNurse(state);
  }
  renderNurseOverlay(state);
  openOverlay('ft-overlay-nurse');
  startNurseTick();
}

function startNurseTick() {
  if (nurseTimer) clearInterval(nurseTimer);
  nurseTimer = setInterval(() => {
    const state = loadActiveNurse();
    if (!state) { clearInterval(nurseTimer); return; }
    updateNurseDisplay(state);
  }, 500);
}

function renderNurseOverlay(state) {
  const thumbL = $('#ft-nurse-thumb-left');
  const thumbR = $('#ft-nurse-thumb-right');
  if (thumbL) thumbL.classList.toggle('ft-nurse-thumb--active', state.activeSide === 'left');
  if (thumbR) thumbR.classList.toggle('ft-nurse-thumb--active', state.activeSide === 'right');
  if (thumbL) thumbL.classList.toggle('ft-nurse-thumb--paused', !!state.paused);
  if (thumbR) thumbR.classList.toggle('ft-nurse-thumb--paused', !!state.paused);
  updateNursePauseBtn(!!state.paused);
  updateNurseDisplay(state);
}

function updateNurseDisplay(state) {
  const { leftMs, rightMs, totalMs } = getNurseTotals(state);

  const display = $('#ft-nurse-display');
  if (display) display.textContent = msToMMSS(totalMs);

  const leftEl  = $('#ft-nurse-left-time');
  const rightEl = $('#ft-nurse-right-time');
  if (leftEl)  leftEl.textContent  = leftMs  > 0 ? msToMMSS(leftMs)  : '—';
  if (rightEl) rightEl.textContent = rightMs > 0 ? msToMMSS(rightMs) : '—';

  const thumbL = $('#ft-nurse-thumb-left');
  const thumbR = $('#ft-nurse-thumb-right');
  if (thumbL) {
    thumbL.classList.toggle('ft-nurse-thumb--active', state.activeSide === 'left');
    thumbL.classList.toggle('ft-nurse-thumb--paused', !!state.paused && state.activeSide === 'left');
  }
  if (thumbR) {
    thumbR.classList.toggle('ft-nurse-thumb--active', state.activeSide === 'right');
    thumbR.classList.toggle('ft-nurse-thumb--paused', !!state.paused && state.activeSide === 'right');
  }
}

function updateNursePauseBtn(paused) {
  const btn = $('#ft-nurse-pause');
  if (!btn) return;
  btn.classList.toggle('ft-nurse-pause-btn--paused', paused);
  const icon = $('#ft-pause-icon');
  if (icon) {
    icon.innerHTML = paused
      ? '<polygon points="5 3 19 12 5 21 5 3"/>'
      : '<rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/>';
  }
}

function pauseNurseSession() {
  const state = loadActiveNurse();
  if (!state || state.paused) return;
  const now = Date.now();
  if (state.activeSide === 'left'  && state.leftStartedAt)  { state.leftAccumulatedMs  += now - state.leftStartedAt;  state.leftStartedAt  = null; }
  if (state.activeSide === 'right' && state.rightStartedAt) { state.rightAccumulatedMs += now - state.rightStartedAt; state.rightStartedAt = null; }
  state.paused = true;
  saveActiveNurse(state);
  updateNurseDisplay(state);
  updateNursePauseBtn(true);
}

function resumeNurseSession() {
  const state = loadActiveNurse();
  if (!state || !state.paused) return;
  const now = Date.now();
  state.paused = false;
  if (state.activeSide === 'left')  state.leftStartedAt  = now;
  if (state.activeSide === 'right') state.rightStartedAt = now;
  saveActiveNurse(state);
  updateNurseDisplay(state);
  updateNursePauseBtn(false);
}

function handleThumbClick(side) {
  const state = loadActiveNurse();
  if (!state) return;
  if (state.paused) {
    const now = Date.now();
    state.paused = false;
    state.activeSide = side;
    if (side === 'left')  { state.leftStartedAt  = now; state.rightStartedAt = null; }
    if (side === 'right') { state.rightStartedAt = now; state.leftStartedAt  = null; }
    saveActiveNurse(state);
    updateNurseDisplay(state);
    updateNursePauseBtn(false);
  } else {
    switchNurseSide(side);
  }
}

function switchNurseSide(side) {
  const state = loadActiveNurse();
  if (!state || state.activeSide === side) return;
  const now = Date.now();
  if (state.activeSide === 'left'  && state.leftStartedAt)  state.leftAccumulatedMs  += now - state.leftStartedAt;
  if (state.activeSide === 'right' && state.rightStartedAt) state.rightAccumulatedMs += now - state.rightStartedAt;
  state.activeSide = side;
  state.leftStartedAt  = side === 'left'  ? now : null;
  state.rightStartedAt = side === 'right' ? now : null;
  saveActiveNurse(state);
  updateNurseDisplay(state);
}

function endNurseSession() {
  const state = loadActiveNurse();
  if (!state) { closeOverlay('ft-overlay-nurse'); return; }
  if (nurseTimer) { clearInterval(nurseTimer); nurseTimer = null; }

  const { leftMs, rightMs, totalMs } = getNurseTotals(state);
  if (totalMs < 5000) {
    saveActiveNurse(null);
    closeOverlay('ft-overlay-nurse');
    showToast('Session too short — not saved.');
    renderHome();
    return;
  }

  saveSession({ id: newId(), type: 'nurse', startedAt: state.sessionStartedAt, endedAt: Date.now(), leftMs, rightMs, totalMs, note: '' });
  saveActiveNurse(null);
  closeOverlay('ft-overlay-nurse');
  showToast('Nursing session saved!');
  renderHome();
  checkWeaningPrompt();
}

// ── BOTTLE OVERLAY ────────────────────────────────────────────────────────────
function openBottleOverlay() {
  const amtEl = $('#ft-bottle-amount');
  if (amtEl) amtEl.value = '0';
  $$('.ft-unit-btn').forEach((b, i) => b.classList.toggle('ft-unit-btn--active', i === 0));
  $$('.ft-type-btn').forEach((b, i) => b.classList.toggle('ft-type-btn--active', i === 0));
  openOverlay('ft-overlay-bottle');
}

function adjustBottleAmount(delta) {
  const amtEl = $('#ft-bottle-amount');
  if (!amtEl) return;
  const unit = $('.ft-unit-btn--active')?.dataset.unit || 'oz';
  const step = unit === 'ml' ? 5 : 0.5;
  let val = parseFloat(amtEl.value || '0') + delta * step;
  val = Math.max(0, Math.round(val * 100) / 100);
  // Keep user-typed precision; only format stepper results
  amtEl.value = unit === 'ml' ? String(val) : String(val);
}

function saveBottleSession() {
  const amtEl  = $('#ft-bottle-amount');
  const unit   = $('.ft-unit-btn--active')?.dataset.unit || 'oz';
  const type   = $('.ft-type-btn--active')?.dataset.type || 'breast_milk';
  const amount = parseFloat(amtEl?.value || '0');
  if (!amount || amount <= 0) { showToast('Please enter an amount.'); return; }
  const amountOz = unit === 'ml' ? amount / 29.5735 : amount;
  saveSession({ id: newId(), type: 'bottle', bottleType: type, amount, amountOz, unit, startedAt: Date.now(), endedAt: Date.now(), note: '' }); // bottle stays in sessions (it's a feed)
  closeOverlay('ft-overlay-bottle');
  showToast('Bottle feed saved!');
  renderHome();
}

// ── PUMP OVERLAY ──────────────────────────────────────────────────────────────
let pumpTimerHandle = null;
let pumpStartedAt   = null;

function openPumpOverlay() {
  pumpStartedAt = null;
  if (pumpTimerHandle) { clearInterval(pumpTimerHandle); pumpTimerHandle = null; }
  const timerEl = $('#ft-pump-timer');
  if (timerEl) timerEl.textContent = '00:00';
  const startBtn = $('#ft-pump-start');
  if (startBtn) { startBtn.textContent = 'Start'; startBtn.dataset.running = '0'; }
  $$('#ft-overlay-pump .ft-pump-amount').forEach(e => e.value = '0');
  openOverlay('ft-overlay-pump');
}

function togglePumpTimer() {
  const btn = $('#ft-pump-start');
  if (!btn) return;
  if (btn.dataset.running === '1') {
    clearInterval(pumpTimerHandle);
    btn.textContent = 'Resume';
    btn.dataset.running = '0';
    pumpStartedAt = null;
  } else {
    pumpStartedAt = Date.now() - (parseInt($('#ft-pump-elapsed')?.textContent || '0') * 1000);
    btn.textContent = 'Pause';
    btn.dataset.running = '1';
    pumpTimerHandle = setInterval(() => {
      const elapsed = Date.now() - pumpStartedAt;
      const timerEl = $('#ft-pump-timer');
      if (timerEl) timerEl.textContent = msToMMSS(elapsed);
      const hidEl = $('#ft-pump-elapsed');
      if (hidEl) hidEl.textContent = Math.floor(elapsed / 1000);
    }, 500);
  }
}

function adjustPumpAmount(side, delta) {
  const amtEl = $(`#ft-pump-${side}`);
  if (!amtEl) return;
  let val = parseFloat(amtEl.value || '0') + delta * 0.5;
  val = Math.max(0, Math.round(val * 100) / 100);
  amtEl.value = String(val);
}

function savePumpSession() {
  if (pumpTimerHandle) { clearInterval(pumpTimerHandle); pumpTimerHandle = null; }
  const leftOz  = parseFloat($('#ft-pump-left')?.value  || '0');
  const rightOz = parseFloat($('#ft-pump-right')?.value || '0');
  const elapsed = parseInt($('#ft-pump-elapsed')?.textContent || '0') * 1000;
  if (elapsed < 1000 && leftOz + rightOz === 0) { showToast('Please log some pump data.'); return; }
  pumpLogs.push({ id: newId(), type: 'pump', leftOz, rightOz, totalOz: leftOz + rightOz, durationMs: elapsed, startedAt: Date.now() - elapsed, endedAt: Date.now(), note: '' });
  savePumpLogs();
  closeOverlay('ft-overlay-pump');
  showToast('Pumping session saved!');
  renderHome();
}

// ── FIRST FOODS OVERLAY ───────────────────────────────────────────────────────
const COMMON_FOODS = ['Avocado', 'Sweet potato', 'Banana', 'Pear', 'Apple', 'Carrot', 'Pumpkin', 'Broccoli', 'Peas', 'Butternut squash', 'Mango', 'Peach', 'Zucchini', 'Rice cereal', 'Oatmeal', 'Chicken', 'Salmon', 'Egg yolk', 'Lentils', 'Greek yogurt'];

function detectAllergen(foodName) {
  const lower = foodName.toLowerCase().trim();
  for (const a of TOP9_ALLERGENS) {
    if (a.keywords.some(k => lower.includes(k))) return a;
  }
  return null;
}

function openFoodOverlay() {
  const nameEl = $('#ft-food-name');
  if (nameEl) nameEl.value = '';

  const flagEl = $('#ft-food-allergen-flag');
  if (flagEl) { flagEl.style.display = 'none'; flagEl.dataset.allergenKey = ''; }

  // Reset pill selections
  [['#ft-food-texture-pills',  '[data-texture]',  0],
   ['#ft-food-amount-pills',   '[data-amount]',   0],
   ['#ft-food-reaction-pills', '[data-reaction]', 0]
  ].forEach(([container, sel, defaultIdx]) => {
    $$(container + ' ' + sel).forEach((b, i) => b.classList.toggle('ft-manual-pill--active', i === defaultIdx));
  });

  const concernWrap = $('#ft-food-concern-wrap');
  if (concernWrap) concernWrap.style.display = 'none';
  const concernText = $('#ft-food-concern-text');
  if (concernText) concernText.value = '';

  const notesEl = $('#ft-food-notes');
  if (notesEl) notesEl.value = '';

  // Populate datalist
  const list = $('#ft-food-list');
  if (list) {
    list.innerHTML = '';
    COMMON_FOODS.forEach(f => {
      const opt = document.createElement('option');
      opt.value = f;
      list.appendChild(opt);
    });
  }

  openOverlay('ft-overlay-food');
}

function saveFoodLog() {
  const nameEl = $('#ft-food-name');
  const foodName = nameEl?.value.trim() || '';
  if (!foodName) { showToast('Please enter a food name.'); return; }

  const texture  = $('.ft-manual-pill--active[data-texture]')?.dataset.texture  || 'puree';
  const amount   = $('.ft-manual-pill--active[data-amount]')?.dataset.amount    || 'few_bites';
  const reaction = $('.ft-manual-pill--active[data-reaction]')?.dataset.reaction || 'ok';
  const concern  = $('#ft-food-concern-text')?.value.trim() || '';
  const notes    = $('#ft-food-notes')?.value.trim() || '';
  const flagEl   = $('#ft-food-allergen-flag');
  const allergenKey = flagEl?.dataset.allergenKey || '';

  const log = {
    id: newId(),
    type: 'food',
    foodName,
    texture,
    amount,
    reaction,
    concernNote: concern,
    notes,
    isAllergen: !!allergenKey,
    allergenKey,
    ts: Date.now(),
    endedAt: Date.now()
  };

  foodLogs.push(log);
  saveFoodLogs();

  if (allergenKey) recordAllergenIntroduction(allergenKey);

  closeOverlay('ft-overlay-food');
  showToast(`${foodName} logged!`);
  renderHome();

  if (reaction === 'concern') {
    showToast('Noted — if you\'re worried, contact your paediatrician.', 4000);
  }
}

function recordAllergenIntroduction(allergenKey) {
  if (!allergens[allergenKey]) {
    allergens[allergenKey] = { introduced: Date.now(), watchUntil: Date.now() + 72 * 60 * 60 * 1000, reactions: [], cleared: false };
  } else {
    allergens[allergenKey].watchUntil = Date.now() + 72 * 60 * 60 * 1000;
  }
  saveAllergens();
}

function getActiveAllergenWatch() {
  const now = Date.now();
  return Object.entries(allergens)
    .filter(([, v]) => !v.cleared && v.watchUntil > now)
    .map(([key, v]) => {
      const allergen = TOP9_ALLERGENS.find(a => a.key === key);
      return { key, label: allergen?.label || key, watchUntil: v.watchUntil, hoursLeft: Math.ceil((v.watchUntil - now) / 3600000) };
    });
}

// ── MEAL OVERLAY ──────────────────────────────────────────────────────────────
function openMealOverlay() {
  $$('#ft-meal-type-pills .ft-manual-pill').forEach((b, i) => b.classList.toggle('ft-manual-pill--active', i === 0));
  $$('#ft-meal-appetite-pills .ft-manual-pill').forEach((b, i) => b.classList.toggle('ft-manual-pill--active', i === 0));
  const foodsEl = $('#ft-meal-foods');
  if (foodsEl) foodsEl.value = '';
  const familyEl = $('#ft-meal-family');
  if (familyEl) familyEl.checked = false;
  const notesEl = $('#ft-meal-notes');
  if (notesEl) notesEl.value = '';
  openOverlay('ft-overlay-meal');
}

function saveMealLog() {
  const mealType = $('.ft-manual-pill--active[data-mealtype]')?.dataset.mealtype || 'meal';
  const foods    = $('#ft-meal-foods')?.value.trim() || '';
  const family   = $('#ft-meal-family')?.checked || false;
  const appetite = $('.ft-manual-pill--active[data-appetite]')?.dataset.appetite || 'ok';
  const notes    = $('#ft-meal-notes')?.value.trim() || '';

  const log = {
    id: newId(),
    type: 'meal',
    mealType,
    foods,
    familyMeal: family,
    appetite,
    notes,
    ts: Date.now(),
    endedAt: Date.now()
  };

  foodLogs.push(log);
  saveFoodLogs();

  closeOverlay('ft-overlay-meal');
  showToast(`${mealType.charAt(0).toUpperCase() + mealType.slice(1)} logged!`);
  renderHome();
}

// ── WATER LOG ─────────────────────────────────────────────────────────────────
function logWater() {
  const log = { id: newId(), type: 'water', ts: Date.now(), endedAt: Date.now() };
  foodLogs.push(log);
  saveFoodLogs();
  showToast('Water logged!');
  renderHome();
}

// ── WEANING DETECTION ─────────────────────────────────────────────────────────
function checkWeaningPrompt() {
  if (prefs.weaningPromptShown) return;
  const months = getAgeMonths();
  if (months < 8) return;

  const fourteenDaysAgo = Date.now() - 14 * 24 * 60 * 60 * 1000;
  const recentNursing = sessions.filter(s => s.type === 'nurse' && s.endedAt >= fourteenDaysAgo);
  const recentDays    = 14;
  const avgPerDay     = recentNursing.length / recentDays;

  if (avgPerDay < 1) {
    const nameEl = $('#ft-weaning-name');
    if (nameEl) nameEl.textContent = prefs.babyName || 'your baby';
    openOverlay('ft-overlay-weaning');
  }
}

// ── HOME SCREEN ───────────────────────────────────────────────────────────────
function renderHome() {
  const stage = getStage();
  renderHeroArea(stage);
  renderSummaryCards(stage);
}

function renderHeroArea(stage) {
  const area = $('#ft-hero-area');
  if (!area) return;

  if (stage >= 4) {
    renderMealHero(area);
  } else {
    renderFeedHero(area);
  }

  // Active allergen watch card (stage 2+)
  if (stage >= 2) {
    const watches = getActiveAllergenWatch();
    if (watches.length > 0) {
      const watchCard = el('div', 'ft-card-watch');
      watchCard.innerHTML = `
        <div class="ft-card-watch__title">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
          Allergen watch active
        </div>
        <div class="ft-card-watch__list">
          ${watches.map(w => `<span class="ft-card-watch__item">${w.label} · ${w.hoursLeft}h left</span>`).join('')}
        </div>
        <p class="ft-card-watch__note">Monitor for hives, swelling, vomiting, or breathing changes. Call your doctor if concerned.</p>`;
      area.appendChild(watchCard);
    }
  }

  // Solids readiness prompt (stage 2, not yet activated)
  if (stage === 2 && !prefs.solidsActivated) {
    const months = getAgeMonths();
    if (months >= 4) {
      const promptCard = el('div', 'ft-card-prompt');
      promptCard.innerHTML = `
        <div class="ft-card-prompt__text">Ready to introduce first foods? Most babies are ready around 4–6 months.</div>
        <button class="ft-card-prompt__cta" id="ft-activate-solids">Start logging first foods</button>`;
      area.appendChild(promptCard);
      $('#ft-activate-solids')?.addEventListener('click', activateSolids);
    }
  }
}

function activateSolids() {
  prefs.solidsActivated = true;
  savePrefs();
  applyStageUI(getStage());
  renderHome();
  showToast('First foods unlocked!');
}

function renderFeedHero(area) {
  const active = loadActiveNurse();

  if (active) {
    const { totalMs } = getNurseTotals(active);
    const heroHTML = `
      <div class="ft-hero" id="ft-hero">
        <div class="ft-hero__label">ACTIVE SESSION</div>
        <div class="ft-hero__row">
          <div class="ft-hero__side-badge">${active.activeSide === 'left' ? 'L' : 'R'}</div>
          <div class="ft-hero__info">
            <div class="ft-hero__breast">Nursing in progress</div>
            <div class="ft-hero__ago">${msToMMSS(totalMs)} elapsed</div>
          </div>
        </div>
        <button class="ft-hero__cta" id="ft-hero-resume">
          <div><div>Resume session</div><div class="ft-hero__cta-sub">Tap to open timer</div></div>
          <div class="ft-hero__cta-arrow"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M9 18l6-6-6-6"/></svg></div>
        </button>
      </div>`;
    area.innerHTML = heroHTML;
    $('#ft-hero-resume')?.addEventListener('click', () => openNurseOverlay());
    return;
  }

  const last = lastSession();
  if (!last) {
    area.innerHTML = `
      <div class="ft-hero ft-hero--empty" id="ft-hero">
        <div class="ft-hero__label">LAST FEED</div>
        <div class="ft-hero__empty-text">No feeds logged yet.<br>Tap a button below to get started.</div>
      </div>`;
    return;
  }

  let badge = '—', breast = '', ago = formatTimeAgo(last.endedAt);

  if (last.type === 'nurse') {
    const dominantLeft = last.leftMs >= last.rightMs;
    badge  = dominantLeft ? 'L' : 'R';
    breast = `Nursing · ${msToHMM(last.totalMs)}`;
    if (last.leftMs > 0 && last.rightMs > 0) {
      breast += ` (L ${msToMMSS(last.leftMs)} / R ${msToMMSS(last.rightMs)})`;
    } else {
      breast += ` ${dominantLeft ? 'Left' : 'Right'}`;
    }
  } else if (last.type === 'bottle') {
    badge  = '🍼';
    breast = `Bottle · ${last.amount} ${last.unit} ${last.bottleType === 'formula' ? 'formula' : 'breast milk'}`;
  } else if (last.type === 'pump') {
    badge  = '⚙';
    breast = `Pump · ${last.totalOz.toFixed(1)} oz total`;
  }

  area.innerHTML = `
    <div class="ft-hero" id="ft-hero">
      <div class="ft-hero__label">LAST FEED</div>
      <div class="ft-hero__row">
        <div class="ft-hero__side-badge">${badge}</div>
        <div class="ft-hero__info">
          <div class="ft-hero__breast">${breast}</div>
          <div class="ft-hero__ago">${ago}</div>
        </div>
      </div>
      <button class="ft-hero__cta" id="ft-hero-next">
        <div><div>Log next feed</div><div class="ft-hero__cta-sub">Tap to record</div></div>
        <div class="ft-hero__cta-arrow"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M9 18l6-6-6-6"/></svg></div>
      </button>
    </div>`;
  $('#ft-hero-next')?.addEventListener('click', openLogPicker);
}

function renderMealHero(area) {
  const todayMeals = todayFoodLogs().filter(f => f.type === 'meal');
  const mealTypes  = ['breakfast', 'lunch', 'dinner', 'snack'];
  const logged     = new Set(todayMeals.map(m => m.mealType));
  const waterToday = todayFoodLogs().filter(f => f.type === 'water').length;

  const chips = mealTypes.map(t => {
    const done = logged.has(t);
    const label = t.charAt(0).toUpperCase() + t.slice(1);
    return `<span class="ft-meal-chip${done ? ' ft-meal-chip--done' : ''}">${done ? '✓ ' : ''}${label}</span>`;
  }).join('');

  area.innerHTML = `
    <div class="ft-hero ft-hero--meal" id="ft-hero">
      <div class="ft-hero__label">TODAY'S MEALS</div>
      <div class="ft-hero__meal-count">${todayMeals.length} meal${todayMeals.length !== 1 ? 's' : ''} logged</div>
      <div class="ft-hero__meal-chips">${chips}</div>
      ${waterToday > 0 ? `<div class="ft-hero__water-note">💧 Water logged ${waterToday}×</div>` : ''}
      <button class="ft-hero__cta" id="ft-hero-log-meal">
        <div><div>Log a meal</div><div class="ft-hero__cta-sub">Tap to record</div></div>
        <div class="ft-hero__cta-arrow"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M9 18l6-6-6-6"/></svg></div>
      </button>
    </div>`;
  $('#ft-hero-log-meal')?.addEventListener('click', openMealOverlay);
}

function renderSummaryCards(stage) {
  const today = todaySessions();

  // Nursing total
  const nurseMs = today.filter(s => s.type === 'nurse').reduce((a, s) => a + s.totalMs, 0);
  const nurseEl = $('#ft-summary-nursing');
  if (nurseEl) {
    if (nurseMs > 0) {
      const h = Math.floor(nurseMs / 3600000);
      const m = Math.floor((nurseMs % 3600000) / 60000);
      nurseEl.innerHTML = `<span class="ft-summary-card__value">${h > 0 ? h : m}</span><span class="ft-summary-card__unit">${h > 0 ? 'h' : 'm'}</span>`;
    } else {
      nurseEl.innerHTML = `<span class="ft-summary-card__value" style="color:var(--ft-muted)">—</span>`;
    }
  }

  // Oz intake
  let ozTotal = 0;
  today.forEach(s => {
    if (s.type === 'bottle') ozTotal += s.amountOz || 0;
    if (s.type === 'pump')   ozTotal += s.totalOz  || 0;
  });
  const ozEl = $('#ft-summary-oz');
  if (ozEl) {
    if (ozTotal > 0) {
      ozEl.innerHTML = `<span class="ft-summary-card__value">${ozTotal.toFixed(1)}</span><span class="ft-summary-card__unit">oz</span>`;
    } else {
      ozEl.innerHTML = `<span class="ft-summary-card__value" style="color:var(--ft-muted)">—</span>`;
    }
  }

  // Feed count — include food/meal for stage 2+
  const foodCount = stage >= 2 ? todayFoodLogs().filter(f => f.type === 'food' || f.type === 'meal').length : 0;
  const countEl = $('#ft-summary-count');
  if (countEl) {
    countEl.innerHTML = `<span class="ft-summary-card__value">${today.length + foodCount}</span>`;
  }

  // Last feed / last log
  const lastSess = lastSession();
  const lastFood = foodLogs.length ? foodLogs[foodLogs.length - 1] : null;
  const lastTs   = Math.max(lastSess?.endedAt || 0, lastFood?.ts || 0);
  const lastEl   = $('#ft-summary-last');
  if (lastEl) {
    if (lastTs > 0) {
      lastEl.innerHTML = `<span class="ft-summary-card__value" style="font-size:1.1rem">${formatTime(lastTs)}</span>`;
    } else {
      lastEl.innerHTML = `<span class="ft-summary-card__value" style="color:var(--ft-muted)">—</span>`;
    }
  }
}

// ── ALLERGEN PASSPORT ─────────────────────────────────────────────────────────
function renderAllergenPassport() {
  const container = $('#ft-allergen-passport');
  if (!container) return;

  const now = Date.now();
  const rows = TOP9_ALLERGENS.map(a => {
    const entry    = allergens[a.key];
    const logs     = foodLogs.filter(f => f.allergenKey === a.key);
    const concerns = logs.filter(f => f.reaction === 'concern').length;

    let status = 'not-tried';
    let statusLabel = 'Not tried';
    let statusClass = '';

    if (entry) {
      if (entry.cleared) {
        status = 'cleared';
        statusLabel = 'Cleared ✓';
        statusClass = 'ft-passport-status--cleared';
      } else if (entry.watchUntil > now) {
        const h = Math.ceil((entry.watchUntil - now) / 3600000);
        status = 'watching';
        statusLabel = `Watching · ${h}h`;
        statusClass = 'ft-passport-status--watching';
      } else {
        status = 'introduced';
        statusLabel = 'Introduced';
        statusClass = 'ft-passport-status--introduced';
      }
    }

    const concernBadge = concerns > 0 ? `<span class="ft-passport-concern">⚠ ${concerns} concern${concerns > 1 ? 's' : ''}</span>` : '';
    return `
      <div class="ft-passport-row">
        <span class="ft-passport-name">${a.label}</span>
        ${concernBadge}
        <span class="ft-passport-status ${statusClass}">${statusLabel}</span>
        ${status === 'introduced' ? `<button class="ft-passport-clear" data-key="${a.key}">Mark cleared</button>` : ''}
      </div>`;
  }).join('');

  container.innerHTML = `
    <div class="ft-passport-wrap">
      <div class="ft-passport-title">Allergen Passport</div>
      <div class="ft-passport-sub">Top-9 allergens — 72h watch after each introduction</div>
      <div class="ft-passport-list">${rows}</div>
    </div>`;

  $$('.ft-passport-clear', container).forEach(btn => {
    btn.addEventListener('click', () => {
      const key = btn.dataset.key;
      if (allergens[key]) { allergens[key].cleared = true; saveAllergens(); }
      renderAllergenPassport();
    });
  });
}

// ── HISTORY SCREEN ────────────────────────────────────────────────────────────
function renderHistory(filter = 'all') {
  const stage = getStage();
  if (stage >= 2) renderAllergenPassport();

  const container = $('#ft-history-list');
  if (!container) return;

  // Merge feeds + food logs (pump is stored separately and shown on its own filter)
  const feedItems = sessions.filter(s => s.type !== 'pump');
  const allItems = [
    ...feedItems.map(s => ({ ...s, _sortTs: s.endedAt })),
    ...foodLogs.map(f => ({ ...f, _sortTs: f.endedAt || f.ts }))
  ].sort((a, b) => b._sortTs - a._sortTs);

  let list = allItems;
  if (filter === 'nurse')  list = list.filter(s => s.type === 'nurse');
  if (filter === 'bottle') list = list.filter(s => s.type === 'bottle');
  if (filter === 'pump')   list = pumpLogs.map(p => ({ ...p, _sortTs: p.endedAt })).sort((a, b) => b._sortTs - a._sortTs);
  if (filter === 'food')   list = list.filter(s => s.type === 'food');
  if (filter === 'meal')   list = list.filter(s => s.type === 'meal');

  if (list.length === 0) {
    container.innerHTML = `<div class="ft-empty-state"><p>No sessions logged yet.</p></div>`;
    return;
  }

  const groups = {};
  list.forEach(s => {
    const key = formatDate(s._sortTs);
    if (!groups[key]) groups[key] = [];
    groups[key].push(s);
  });

  container.innerHTML = '';
  Object.entries(groups).forEach(([date, items]) => {
    const group = el('div', 'ft-history-group');
    group.innerHTML = `<div class="ft-history-date">${date}</div>`;
    items.forEach(s => {
      const row = el('div', 'ft-history-row');
      row.innerHTML = historyRowHTML(s);
      const del = $('.ft-history-delete', row);
      if (del) del.addEventListener('click', () => deleteEntry(s.id, s.type));
      group.appendChild(row);
    });
    container.appendChild(group);
  });
}

function historyRowHTML(s) {
  const del = `<button class="ft-history-delete" title="Delete"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6L6 18M6 6l12 12"/></svg></button>`;
  const ts  = s._sortTs || s.endedAt || s.ts;

  if (s.type === 'nurse') {
    const detail = [s.leftMs > 0 ? `L ${msToMMSS(s.leftMs)}` : '', s.rightMs > 0 ? `R ${msToMMSS(s.rightMs)}` : ''].filter(Boolean).join(' · ');
    return `<div class="ft-history-icon ft-history-icon--nurse"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 3"/></svg></div><div class="ft-history-meta"><div class="ft-history-title">Nursing · ${msToHMM(s.totalMs)}</div><div class="ft-history-detail">${detail}</div></div><div class="ft-history-time">${formatTime(ts)}</div>${del}`;
  }
  if (s.type === 'bottle') {
    return `<div class="ft-history-icon ft-history-icon--bottle"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M8 2h8l1 4H7L8 2z"/><rect x="6" y="6" width="12" height="14" rx="4"/></svg></div><div class="ft-history-meta"><div class="ft-history-title">Bottle · ${s.amount} ${s.unit}</div><div class="ft-history-detail">${s.bottleType === 'formula' ? 'Formula' : s.bottleType === 'cows_milk' ? "Cow's milk" : 'Breast milk'}</div></div><div class="ft-history-time">${formatTime(ts)}</div>${del}`;
  }
  if (s.type === 'pump') {
    return `<div class="ft-history-icon ft-history-icon--pump"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="9"/><path d="M8 12h8M12 8v8"/></svg></div><div class="ft-history-meta"><div class="ft-history-title">Pump · ${s.totalOz.toFixed(1)} oz</div><div class="ft-history-detail">${s.leftOz > 0 ? `L ${s.leftOz}` : ''} ${s.rightOz > 0 ? `R ${s.rightOz}` : ''} oz</div></div><div class="ft-history-time">${formatTime(ts)}</div>${del}`;
  }
  if (s.type === 'food') {
    const allergenBadge = s.isAllergen ? `<span class="ft-history-allergen">⚠ ${TOP9_ALLERGENS.find(a => a.key === s.allergenKey)?.label || s.allergenKey}</span>` : '';
    const reactionMap = { loved: '😊', ok: '🙂', refused: '🙅', concern: '⚠️' };
    return `<div class="ft-history-icon ft-history-icon--food">🥣</div><div class="ft-history-meta"><div class="ft-history-title">First food · ${s.foodName}</div><div class="ft-history-detail">${reactionMap[s.reaction] || ''} ${s.amount?.replace('_', ' ')} ${allergenBadge}</div></div><div class="ft-history-time">${formatTime(ts)}</div>${del}`;
  }
  if (s.type === 'meal') {
    const mealLabel = s.mealType.charAt(0).toUpperCase() + s.mealType.slice(1);
    const appetiteMap = { great: '😋', ok: '🙂', barely: '😕' };
    return `<div class="ft-history-icon ft-history-icon--meal">🍽️</div><div class="ft-history-meta"><div class="ft-history-title">${mealLabel}${s.foods ? ` · ${s.foods}` : ''}</div><div class="ft-history-detail">${appetiteMap[s.appetite] || ''} ${s.familyMeal ? 'Family meal' : ''}</div></div><div class="ft-history-time">${formatTime(ts)}</div>${del}`;
  }
  if (s.type === 'water') {
    return `<div class="ft-history-icon ft-history-icon--water">💧</div><div class="ft-history-meta"><div class="ft-history-title">Water</div><div class="ft-history-detail"></div></div><div class="ft-history-time">${formatTime(ts)}</div>${del}`;
  }
  return '';
}

function deleteEntry(id, type) {
  if (type === 'food' || type === 'meal' || type === 'water') {
    foodLogs = foodLogs.filter(f => f.id !== id);
    saveFoodLogs();
  } else {
    sessions = sessions.filter(s => s.id !== id);
    saveSessions();
  }
  renderHistory();
  renderHome();
}

// ── INSIGHTS SCREEN ───────────────────────────────────────────────────────────
function renderInsights(period = '7d') {
  const container = $('#ft-insights-content');
  if (!container) return;

  const stage    = getStage();
  const now      = Date.now();
  const periodMs = period === '24h' ? 86400000 : period === '7d' ? 7 * 86400000 : 30 * 86400000;
  const days     = period === '24h' ? 1 : period === '7d' ? 7 : 30;

  const filtered     = sessions.filter(s => s.endedAt >= now - periodMs);
  const filteredFood = foodLogs.filter(f => (f.endedAt || f.ts) >= now - periodMs);

  if (filtered.length === 0 && filteredFood.length === 0) {
    container.innerHTML = `<div class="ft-empty-state"><p>No data for this period yet.<br>Start logging to see insights.</p></div>`;
    return;
  }

  const nurseItems  = filtered.filter(s => s.type === 'nurse');
  const bottleItems = filtered.filter(s => s.type === 'bottle');
  const foodItems   = filteredFood.filter(f => f.type === 'food');
  const mealItems   = filteredFood.filter(f => f.type === 'meal');
  const waterItems  = filteredFood.filter(f => f.type === 'water');

  // pump is stored separately — filter by same date window
  const nowMs = Date.now();
  const pumpFiltered = pumpLogs.filter(p => p.endedAt >= nowMs - days * 24 * 60 * 60 * 1000);

  const totalNurseMs = nurseItems.reduce((a, s) => a + s.totalMs, 0);
  const totalOz      = bottleItems.reduce((a, s) => a + (s.amountOz || 0), 0);
  const avgPerDay    = (filtered.length / days).toFixed(1);

  let avgGapMins = 0;
  if (filtered.length > 1) {
    const sorted = [...filtered].sort((a, b) => a.endedAt - b.endedAt);
    let totalGap = 0;
    for (let i = 1; i < sorted.length; i++) totalGap += sorted[i].startedAt - sorted[i - 1].endedAt;
    avgGapMins = Math.round(totalGap / (sorted.length - 1) / 60000);
  }

  const barData = buildDailyBars(filtered, days);

  let html = `
    <div class="ft-insights-narrative">${buildNarrative(filtered, period, avgGapMins)}</div>
    <div class="ft-metrics-grid">
      <div class="ft-metric-card">
        <div class="ft-metric-value">${avgPerDay}</div>
        <div class="ft-metric-label">Feeds / day</div>
      </div>
      <div class="ft-metric-card">
        <div class="ft-metric-value">${totalNurseMs > 0 ? msToHMM(totalNurseMs / days) : '—'}</div>
        <div class="ft-metric-label">Nursing / day</div>
      </div>
      <div class="ft-metric-card">
        <div class="ft-metric-value">${totalOz > 0 ? (totalOz / days).toFixed(1) : '—'}</div>
        <div class="ft-metric-label">Oz / day</div>
      </div>
      <div class="ft-metric-card">
        <div class="ft-metric-value">${avgGapMins > 0 ? (avgGapMins >= 60 ? `${Math.floor(avgGapMins / 60)}h${avgGapMins % 60 > 0 ? ` ${avgGapMins % 60}m` : ''}` : `${avgGapMins}m`) : '—'}</div>
        <div class="ft-metric-label">Avg. gap</div>
      </div>
    </div>
    ${buildBarChart(barData)}`;

  // Stage 2+: food variety
  if (stage >= 2 && foodItems.length > 0) {
    const uniqueFoods  = [...new Set(foodItems.map(f => f.foodName))];
    const allergenCount = foodItems.filter(f => f.isAllergen).length;
    html += `
      <div class="ft-insights-section">
        <div class="ft-insights-section__title">First Foods</div>
        <div class="ft-metrics-grid">
          <div class="ft-metric-card">
            <div class="ft-metric-value">${uniqueFoods.length}</div>
            <div class="ft-metric-label">Unique foods</div>
          </div>
          <div class="ft-metric-card">
            <div class="ft-metric-value">${allergenCount}</div>
            <div class="ft-metric-label">Allergen intros</div>
          </div>
          <div class="ft-metric-card">
            <div class="ft-metric-value">${Object.keys(allergens).filter(k => allergens[k].cleared).length}</div>
            <div class="ft-metric-label">Allergens cleared</div>
          </div>
        </div>
      </div>`;
  }

  // Stage 3+: meals
  if (stage >= 3 && mealItems.length > 0) {
    const perDay = (mealItems.length / days).toFixed(1);
    const great  = mealItems.filter(m => m.appetite === 'great').length;
    const pct    = Math.round((great / mealItems.length) * 100);
    html += `
      <div class="ft-insights-section">
        <div class="ft-insights-section__title">Meals</div>
        <div class="ft-metrics-grid">
          <div class="ft-metric-card">
            <div class="ft-metric-value">${perDay}</div>
            <div class="ft-metric-label">Meals / day</div>
          </div>
          <div class="ft-metric-card">
            <div class="ft-metric-value">${pct}%</div>
            <div class="ft-metric-label">Great appetite</div>
          </div>
          <div class="ft-metric-card">
            <div class="ft-metric-value">${waterItems.length}</div>
            <div class="ft-metric-label">Water logs</div>
          </div>
        </div>
      </div>`;
  }

  container.innerHTML = html;
}

function buildDailyBars(items, days) {
  const bars = [];
  for (let i = days - 1; i >= 0; i--) {
    const dayStart = new Date(); dayStart.setHours(0, 0, 0, 0); dayStart.setDate(dayStart.getDate() - i);
    const dayEnd   = new Date(dayStart); dayEnd.setDate(dayEnd.getDate() + 1);
    const count    = items.filter(s => s.endedAt >= dayStart.getTime() && s.endedAt < dayEnd.getTime()).length;
    const label    = i === 0 ? 'Today' : dayStart.toLocaleDateString([], { weekday: 'short' });
    bars.push({ label, count });
  }
  return bars;
}

function buildBarChart(bars) {
  const max = Math.max(...bars.map(b => b.count), 1);
  const barHTML = bars.map(b => {
    const pct = Math.round((b.count / max) * 100);
    return `<div class="ft-bar-col"><div class="ft-bar-count">${b.count || ''}</div><div class="ft-bar-track"><div class="ft-bar-fill" style="height:${pct}%"></div></div><div class="ft-bar-label">${b.label}</div></div>`;
  }).join('');
  return `<div class="ft-bar-chart">${barHTML}</div>`;
}

function buildNarrative(items, period, avgGapMins) {
  const count  = items.length;
  const days   = period === '24h' ? 1 : period === '7d' ? 7 : 30;
  const perDay = (count / days).toFixed(1);
  let text = `You've logged <strong>${count} feed${count !== 1 ? 's' : ''}</strong> over the past ${period === '24h' ? '24 hours' : period === '7d' ? '7 days' : '30 days'} — about <strong>${perDay} per day</strong>.`;
  if (avgGapMins > 0) {
    const gapStr = avgGapMins >= 60 ? `${Math.floor(avgGapMins / 60)}h ${avgGapMins % 60}m` : `${avgGapMins} minutes`;
    text += ` Average time between feeds: <strong>${gapStr}</strong>.`;
  }
  return text;
}

// ── VOICE INPUT ───────────────────────────────────────────────────────────────
function parseVoiceInput(text) {
  const t = text.toLowerCase().trim();
  const numMatch = (str) => { const m = str.match(/(\d+(?:\.\d+)?)/); return m ? parseFloat(m[1]) : 0; };

  if (/nurse|breast|feed|left|right/.test(t)) {
    const side  = /right/.test(t) ? 'right' : 'left';
    const mins  = numMatch(t.replace(/hour|hr/, ' 60 minute'));
    return { action: 'nurse_quick', side, minutes: mins || 10 };
  }
  if (/bottle|formula|breast.?milk/.test(t)) {
    const oz = /ml|milliliter/.test(t) ? numMatch(t) / 29.5735 : numMatch(t);
    return { action: 'bottle', amountOz: oz || 3, type: /formula/.test(t) ? 'formula' : 'breast_milk' };
  }
  if (/pump/.test(t)) {
    const leftMatch  = t.match(/left[^,]*?(\d+(?:\.\d+)?)/);
    const rightMatch = t.match(/right[^,]*?(\d+(?:\.\d+)?)/);
    return { action: 'pump', leftOz: leftMatch ? parseFloat(leftMatch[1]) : 0, rightOz: rightMatch ? parseFloat(rightMatch[1]) : 0 };
  }
  return { action: 'unknown', text };
}

function openVoiceOverlay() {
  openOverlay('ft-overlay-voice');
  const SpeechRecog = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!SpeechRecog) { $('#ft-voice-transcript').textContent = 'Voice input is not supported in this browser.'; return; }

  const statusEl = $('#ft-voice-status');
  const transcEl = $('#ft-voice-transcript');
  if (statusEl) statusEl.textContent = 'Listening…';
  if (transcEl) transcEl.textContent = '';

  voiceRec = new SpeechRecog();
  voiceRec.lang = 'en-US';
  voiceRec.interimResults = true;
  voiceRec.maxAlternatives = 1;

  voiceRec.onresult = (event) => {
    const last = event.results[event.results.length - 1];
    const transcript = last[0].transcript;
    if (transcEl) transcEl.textContent = transcript;
    if (last.isFinal) {
      if (statusEl) statusEl.textContent = 'Got it! Saving…';
      handleVoiceResult(transcript);
    }
  };

  voiceRec.onerror = (event) => { if (statusEl) statusEl.textContent = `Error: ${event.error}`; };
  voiceRec.onend = () => { const orb = $('.ft-voice-orb'); if (orb) orb.classList.remove('ft-voice-orb--active', 'ft-voice-orb--listening'); };

  const orb = $('.ft-voice-orb');
  if (orb) orb.classList.add('ft-voice-orb--active', 'ft-voice-orb--listening');
  voiceRec.start();
}

function handleVoiceResult(text) {
  const parsed = parseVoiceInput(text);
  setTimeout(() => {
    closeOverlay('ft-overlay-voice');
    if (voiceRec) { try { voiceRec.stop(); } catch {} voiceRec = null; }

    if (parsed.action === 'nurse_quick') {
      const totalMs = parsed.minutes * 60000;
      saveSession({ id: newId(), type: 'nurse', startedAt: Date.now() - totalMs, endedAt: Date.now(), leftMs: parsed.side === 'left' ? totalMs : 0, rightMs: parsed.side === 'right' ? totalMs : 0, totalMs, note: `voice: "${text}"` });
      showToast(`Nursing ${parsed.side} ${parsed.minutes}m saved!`);
    } else if (parsed.action === 'bottle') {
      saveSession({ id: newId(), type: 'bottle', bottleType: parsed.type, amount: Math.round(parsed.amountOz * 10) / 10, amountOz: parsed.amountOz, unit: 'oz', startedAt: Date.now(), endedAt: Date.now(), note: `voice: "${text}"` });
      showToast(`Bottle ${parsed.amountOz.toFixed(1)} oz saved!`);
    } else if (parsed.action === 'pump') {
      pumpLogs.push({ id: newId(), type: 'pump', leftOz: parsed.leftOz, rightOz: parsed.rightOz, totalOz: parsed.leftOz + parsed.rightOz, durationMs: 0, startedAt: Date.now(), endedAt: Date.now(), note: `voice: "${text}"` });
      savePumpLogs();
      showToast(`Pump ${(parsed.leftOz + parsed.rightOz).toFixed(1)} oz saved!`);
    } else {
      showToast('Didn\'t catch that — try again.');
      return;
    }
    renderHome();
  }, 800);
}

function closeVoiceOverlay() {
  if (voiceRec) { try { voiceRec.stop(); } catch {} voiceRec = null; }
  closeOverlay('ft-overlay-voice');
}

// ── MANUAL ENTRY OVERLAY ─────────────────────────────────────────────────────
let manualFeedType   = 'nurse';
let manualNurseSide  = 'left';
let manualBottleType = 'breast_milk';
let manualBottleUnit = 'oz';

function openManualOverlay() {
  manualFeedType   = 'nurse';
  manualNurseSide  = 'left';
  manualBottleType = 'breast_milk';
  manualBottleUnit = 'oz';

  const localISO = new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  ['#ft-manual-nurse-time', '#ft-manual-bottle-time', '#ft-manual-pump-time'].forEach(sel => {
    const e = $(sel); if (e) e.value = localISO;
  });
  [['#ft-manual-nurse-duration', ''], ['#ft-manual-bottle-amount', ''], ['#ft-manual-pump-duration', ''], ['#ft-manual-pump-left', ''], ['#ft-manual-pump-right', '']].forEach(([sel, val]) => {
    const e = $(sel); if (e) e.value = val;
  });

  $$('.ft-manual-tab').forEach(t => t.classList.toggle('ft-manual-tab--active', t.dataset.mtype === 'nurse'));
  $$('.ft-manual-fields').forEach(f => f.classList.add('ft-manual-fields--hidden'));
  $('#ft-manual-fields-nurse')?.classList.remove('ft-manual-fields--hidden');
  $$('.ft-manual-pill[data-side]').forEach((b, i) => b.classList.toggle('ft-manual-pill--active', i === 0));
  $$('.ft-manual-pill[data-btype]').forEach((b, i) => b.classList.toggle('ft-manual-pill--active', i === 0));
  $$('.ft-manual-unit').forEach((b, i) => b.classList.toggle('ft-manual-unit--active', i === 0));

  openOverlay('ft-overlay-manual');
}

function switchManualTab(type) {
  manualFeedType = type;
  $$('.ft-manual-tab').forEach(t => t.classList.toggle('ft-manual-tab--active', t.dataset.mtype === type));
  $$('.ft-manual-fields').forEach(f => f.classList.add('ft-manual-fields--hidden'));
  $(`#ft-manual-fields-${type}`)?.classList.remove('ft-manual-fields--hidden');
}

function saveManualEntry() {
  if (manualFeedType === 'nurse')  saveManualNurse();
  else if (manualFeedType === 'bottle') saveManualBottle();
  else saveManualPump();
}

function saveManualNurse() {
  const durationMin = parseFloat($('#ft-manual-nurse-duration')?.value || '0');
  const timeVal     = $('#ft-manual-nurse-time')?.value;
  if (!durationMin || durationMin <= 0) { showToast('Please enter a duration.'); return; }
  const endedAt = timeVal ? new Date(timeVal).getTime() : Date.now();
  const totalMs = durationMin * 60000;
  const leftMs  = manualNurseSide === 'right' ? 0 : manualNurseSide === 'both' ? totalMs / 2 : totalMs;
  const rightMs = manualNurseSide === 'left'  ? 0 : manualNurseSide === 'both' ? totalMs / 2 : totalMs;
  saveSession({ id: newId(), type: 'nurse', startedAt: endedAt - totalMs, endedAt, leftMs, rightMs, totalMs, note: '', source: 'manual' });
  closeOverlay('ft-overlay-manual');
  showToast('Nursing session saved!');
  renderHome();
}

function saveManualBottle() {
  const amount  = parseFloat($('#ft-manual-bottle-amount')?.value || '0');
  const timeVal = $('#ft-manual-bottle-time')?.value;
  if (!amount || amount <= 0) { showToast('Please enter an amount.'); return; }
  const endedAt  = timeVal ? new Date(timeVal).getTime() : Date.now();
  const amountOz = manualBottleUnit === 'ml' ? amount / 29.5735 : amount;
  saveSession({ id: newId(), type: 'bottle', bottleType: manualBottleType, amount, amountOz, unit: manualBottleUnit, startedAt: endedAt, endedAt, note: '', source: 'manual' });
  closeOverlay('ft-overlay-manual');
  showToast('Bottle feed saved!');
  renderHome();
}

function saveManualPump() {
  const durationMin = parseFloat($('#ft-manual-pump-duration')?.value || '0');
  const leftOz      = parseFloat($('#ft-manual-pump-left')?.value   || '0');
  const rightOz     = parseFloat($('#ft-manual-pump-right')?.value  || '0');
  const timeVal     = $('#ft-manual-pump-time')?.value;
  if (durationMin <= 0 && leftOz + rightOz <= 0) { showToast('Please enter a duration or amounts.'); return; }
  const endedAt    = timeVal ? new Date(timeVal).getTime() : Date.now();
  const durationMs = durationMin * 60000;
  pumpLogs.push({ id: newId(), type: 'pump', leftOz, rightOz, totalOz: leftOz + rightOz, durationMs, startedAt: endedAt - durationMs, endedAt, note: '', source: 'manual' });
  savePumpLogs();
  closeOverlay('ft-overlay-manual');
  showToast('Pumping session saved!');
  renderHome();
}

// ── TODAY SUMMARY ─────────────────────────────────────────────────────────────
function buildTodaySummaryHTML() {
  const todayStart = new Date(); todayStart.setHours(0,0,0,0);
  const todaySessions = sessions.filter(s => s.startedAt >= todayStart.getTime() && s.type !== 'pump');
  const todayPumps    = pumpLogs.filter(p => p.startedAt >= todayStart.getTime());

  const nurseSessions  = todaySessions.filter(s => s.type === 'nurse');
  const bottleSessions = todaySessions.filter(s => s.type === 'bottle');

  const totalNurseMs = nurseSessions.reduce((a, s) => a + (s.totalMs || 0), 0);
  const totalOz  = bottleSessions.reduce((a, s) => a + (s.amountOz || 0), 0);
  const pumpOz   = todayPumps.reduce((a, p) => a + ((p.leftOz || 0) + (p.rightOz || 0)), 0);

  const rows = [];
  rows.push(`<div class="ft-ts-row"><span class="ft-ts-label">Total feeds today</span><span class="ft-ts-value">${todaySessions.length}</span></div>`);
  if (nurseSessions.length > 0)
    rows.push(`<div class="ft-ts-row"><span class="ft-ts-label">Nursing sessions</span><span class="ft-ts-value">${nurseSessions.length} · ${msToHMM(totalNurseMs)}</span></div>`);
  if (bottleSessions.length > 0)
    rows.push(`<div class="ft-ts-row"><span class="ft-ts-label">Bottle total</span><span class="ft-ts-value">${totalOz.toFixed(1)} oz</span></div>`);
  if (todayPumps.length > 0)
    rows.push(`<div class="ft-ts-row"><span class="ft-ts-label">Pumped today</span><span class="ft-ts-value">${pumpOz.toFixed(1)} oz</span></div>`);
  if (rows.length === 1 && todaySessions.length === 0)
    return '<p style="color:var(--ft-muted);text-align:center;margin:0">No feeds logged today yet.</p>';
  return rows.join('');
}

// ── EVENT BINDING ─────────────────────────────────────────────────────────────
function bindEvents() {
  // Bottom nav
  $$('.ft-nav-item[data-screen]').forEach(btn => btn.addEventListener('click', () => navigate(btn.dataset.screen)));
  $('.ft-nav-add')?.addEventListener('click', openLogPicker);

  // Quick log buttons
  $('#ft-ql-nurse')?.addEventListener('click',  () => openNurseOverlay('left'));
  $('#ft-ql-bottle')?.addEventListener('click', () => openBottleOverlay());
  $('#ft-ql-pump')?.addEventListener('click',   () => openPumpOverlay());
  $('#ft-ql-food')?.addEventListener('click',   () => openFoodOverlay());
  $('#ft-ql-meal')?.addEventListener('click',   () => openMealOverlay());
  $('#ft-ql-water')?.addEventListener('click',  () => logWater());

  // Voice nav
  $('#ft-nav-voice')?.addEventListener('click', () => openVoiceOverlay());

  // Log picker
  $('#ft-pick-nurse-l')?.addEventListener('click', () => { closeLogPicker(); openNurseOverlay('left'); });
  $('#ft-pick-nurse-r')?.addEventListener('click', () => { closeLogPicker(); openNurseOverlay('right'); });
  $('#ft-pick-bottle')?.addEventListener('click',  () => { closeLogPicker(); openBottleOverlay(); });
  $('#ft-pick-pump')?.addEventListener('click',    () => { closeLogPicker(); openPumpOverlay(); });
  $('#ft-pick-food')?.addEventListener('click',    () => { closeLogPicker(); openFoodOverlay(); });
  $('#ft-pick-meal')?.addEventListener('click',    () => { closeLogPicker(); openMealOverlay(); });
  $('#ft-pick-water')?.addEventListener('click',   () => { closeLogPicker(); logWater(); });
  $('#ft-pick-voice')?.addEventListener('click',   () => { closeLogPicker(); openVoiceOverlay(); });
  $('#ft-pick-manual')?.addEventListener('click',  () => { closeLogPicker(); openManualOverlay(); });
  $('#ft-pick-close')?.addEventListener('click',   closeLogPicker);

  // Nurse overlay
  $('#ft-nurse-thumb-left')?.addEventListener('click',  () => handleThumbClick('left'));
  $('#ft-nurse-thumb-right')?.addEventListener('click', () => handleThumbClick('right'));
  $('#ft-nurse-pause')?.addEventListener('click', () => {
    const state = loadActiveNurse();
    if (!state) return;
    if (state.paused) resumeNurseSession();
    else pauseNurseSession();
  });
  $('#ft-nurse-end')?.addEventListener('click', endNurseSession);
  $('#ft-nurse-close')?.addEventListener('click', () => { if (nurseTimer) { clearInterval(nurseTimer); nurseTimer = null; } closeOverlay('ft-overlay-nurse'); });

  // Desktop strip
  $('#ft-dstrip-history')?.addEventListener('click',  () => navigate('history'));
  $('#ft-dstrip-insights')?.addEventListener('click', () => navigate('insights'));
  $('#ft-dstrip-voice')?.addEventListener('click',    () => openVoiceOverlay());

  // Today summary accordion
  const summaryToggle  = $('#ft-today-summary-toggle');
  const summaryContent = $('#ft-today-summary-content');
  if (summaryToggle && summaryContent) {
    summaryToggle.addEventListener('click', () => {
      const expanded = summaryToggle.getAttribute('aria-expanded') === 'true';
      summaryToggle.setAttribute('aria-expanded', String(!expanded));
      if (!expanded) {
        summaryContent.removeAttribute('hidden');
        summaryContent.innerHTML = buildTodaySummaryHTML();
      } else {
        summaryContent.setAttribute('hidden', '');
      }
    });
  }

  // Bottle overlay
  $('#ft-bottle-minus')?.addEventListener('click', () => adjustBottleAmount(-1));
  $('#ft-bottle-plus')?.addEventListener('click',  () => adjustBottleAmount(1));
  $('#ft-bottle-save')?.addEventListener('click',  saveBottleSession);
  $('#ft-bottle-close')?.addEventListener('click', () => closeOverlay('ft-overlay-bottle'));
  $$('.ft-unit-btn').forEach(btn => btn.addEventListener('click', () => { $$('.ft-unit-btn').forEach(b => b.classList.remove('ft-unit-btn--active')); btn.classList.add('ft-unit-btn--active'); const amtEl = $('#ft-bottle-amount'); if (amtEl) amtEl.textContent = '0'; }));
  $$('.ft-type-btn').forEach(btn => btn.addEventListener('click', () => { $$('.ft-type-btn').forEach(b => b.classList.remove('ft-type-btn--active')); btn.classList.add('ft-type-btn--active'); }));

  // Pump overlay
  $('#ft-pump-start')?.addEventListener('click',      togglePumpTimer);
  $('#ft-pump-left-minus')?.addEventListener('click',  () => adjustPumpAmount('left', -1));
  $('#ft-pump-left-plus')?.addEventListener('click',   () => adjustPumpAmount('left', 1));
  $('#ft-pump-right-minus')?.addEventListener('click', () => adjustPumpAmount('right', -1));
  $('#ft-pump-right-plus')?.addEventListener('click',  () => adjustPumpAmount('right', 1));
  $('#ft-pump-save')?.addEventListener('click',  savePumpSession);
  $('#ft-pump-close')?.addEventListener('click', () => { if (pumpTimerHandle) { clearInterval(pumpTimerHandle); pumpTimerHandle = null; } closeOverlay('ft-overlay-pump'); });

  // First foods overlay
  $('#ft-food-close')?.addEventListener('click', () => closeOverlay('ft-overlay-food'));
  $('#ft-food-save')?.addEventListener('click',  saveFoodLog);
  $('#ft-food-name')?.addEventListener('input', (e) => {
    const allergen = detectAllergen(e.target.value);
    const flagEl   = $('#ft-food-allergen-flag');
    if (flagEl) {
      flagEl.style.display = allergen ? '' : 'none';
      flagEl.dataset.allergenKey = allergen?.key || '';
    }
  });
  $$('#ft-food-texture-pills .ft-manual-pill').forEach(btn => btn.addEventListener('click', () => { $$('#ft-food-texture-pills .ft-manual-pill').forEach(b => b.classList.remove('ft-manual-pill--active')); btn.classList.add('ft-manual-pill--active'); }));
  $$('#ft-food-amount-pills .ft-manual-pill').forEach(btn => btn.addEventListener('click', () => { $$('#ft-food-amount-pills .ft-manual-pill').forEach(b => b.classList.remove('ft-manual-pill--active')); btn.classList.add('ft-manual-pill--active'); }));
  $$('#ft-food-reaction-pills .ft-manual-pill').forEach(btn => btn.addEventListener('click', () => {
    $$('#ft-food-reaction-pills .ft-manual-pill').forEach(b => b.classList.remove('ft-manual-pill--active'));
    btn.classList.add('ft-manual-pill--active');
    const concernWrap = $('#ft-food-concern-wrap');
    if (concernWrap) concernWrap.style.display = btn.dataset.reaction === 'concern' ? '' : 'none';
  }));

  // Meal overlay
  $('#ft-meal-close')?.addEventListener('click', () => closeOverlay('ft-overlay-meal'));
  $('#ft-meal-save')?.addEventListener('click',  saveMealLog);
  $$('#ft-meal-type-pills .ft-manual-pill').forEach(btn => btn.addEventListener('click', () => { $$('#ft-meal-type-pills .ft-manual-pill').forEach(b => b.classList.remove('ft-manual-pill--active')); btn.classList.add('ft-manual-pill--active'); }));
  $$('#ft-meal-appetite-pills .ft-manual-pill').forEach(btn => btn.addEventListener('click', () => { $$('#ft-meal-appetite-pills .ft-manual-pill').forEach(b => b.classList.remove('ft-manual-pill--active')); btn.classList.add('ft-manual-pill--active'); }));

  // Voice overlay
  $('#ft-voice-close')?.addEventListener('click', closeVoiceOverlay);

  // Manual entry overlay
  $('#ft-manual-close')?.addEventListener('click', () => closeOverlay('ft-overlay-manual'));
  $('#ft-manual-save')?.addEventListener('click',  saveManualEntry);
  $$('.ft-manual-tab').forEach(tab => tab.addEventListener('click', () => switchManualTab(tab.dataset.mtype)));
  $$('.ft-manual-pill[data-side]').forEach(btn => btn.addEventListener('click', () => { manualNurseSide = btn.dataset.side; $$('.ft-manual-pill[data-side]').forEach(b => b.classList.toggle('ft-manual-pill--active', b === btn)); }));
  $$('.ft-manual-pill[data-btype]').forEach(btn => btn.addEventListener('click', () => { manualBottleType = btn.dataset.btype; $$('.ft-manual-pill[data-btype]').forEach(b => b.classList.toggle('ft-manual-pill--active', b === btn)); }));
  $$('.ft-manual-unit').forEach(btn => btn.addEventListener('click', () => { manualBottleUnit = btn.dataset.bunit; $$('.ft-manual-unit').forEach(b => b.classList.toggle('ft-manual-unit--active', b === btn)); }));

  // History filter chips
  $$('.ft-filter-chip').forEach(chip => chip.addEventListener('click', () => { $$('.ft-filter-chip').forEach(c => c.classList.remove('ft-filter-chip--active')); chip.classList.add('ft-filter-chip--active'); renderHistory(chip.dataset.filter || 'all'); }));

  // Insights period toggle
  $$('.ft-period-btn').forEach(btn => btn.addEventListener('click', () => { $$('.ft-period-btn').forEach(b => b.classList.remove('ft-period-btn--active')); btn.classList.add('ft-period-btn--active'); renderInsights(btn.dataset.period); }));

  // Onboarding
  $('#ft-ob-save')?.addEventListener('click', saveOnboarding);
  $('#ft-ob-close')?.addEventListener('click', () => closeOverlay('ft-onboarding'));
  $$('.ft-ob-fm').forEach(btn => btn.addEventListener('click', () => { $$('.ft-ob-fm').forEach(b => b.classList.remove('ft-ob-fm--active')); btn.classList.add('ft-ob-fm--active'); }));

  // Weaning overlay
  $('#ft-weaning-yes')?.addEventListener('click',     () => { prefs.weaningPromptShown = true; savePrefs(); closeOverlay('ft-overlay-weaning'); showToast('Congratulations on this milestone! ✨'); });
  $('#ft-weaning-partly')?.addEventListener('click',  () => { prefs.weaningPromptShown = true; savePrefs(); closeOverlay('ft-overlay-weaning'); });
  $('#ft-weaning-forgot')?.addEventListener('click',  () => { prefs.weaningPromptShown = true; savePrefs(); closeOverlay('ft-overlay-weaning'); });
  $('#ft-weaning-dismiss')?.addEventListener('click', () => { prefs.weaningPromptShown = true; savePrefs(); closeOverlay('ft-overlay-weaning'); });
  $('#ft-weaning-close-bg')?.addEventListener('click', () => closeOverlay('ft-overlay-weaning'));
}

// ── BOOT ──────────────────────────────────────────────────────────────────────
async function boot() {
  loadSessions();
  loadPumpLogs();
  loadFoodLogs();
  loadAllergens();
  loadPrefs();

  // Before showing onboarding, check if another tracker already saved a child
  if (!prefs.onboardingDone) {
    await hydrateFromSharedProfile();
  }

  updateTopbar();
  bindEvents();

  const stage = getStage();
  applyStageUI(stage);

  if (loadActiveNurse()) startNurseTick();

  // Wire the child-switcher button
  const switchBtn = $('#ft-switch-child-btn');
  if (switchBtn) switchBtn.addEventListener('click', switchActiveChild);

  checkOnboarding();
  navigate('home');

  // Hydrate from cloud for the active child, then migrate any local data.
  loadFromCloud().then(() => {
    updateTopbar();
    navigate(screen);
    syncToCloud();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}
